/**
 * The phone lines a person in crisis can call — per destination the platform
 * opens, and a European fallback (owner, 21 Oct 2026: D2).
 *
 * The CoPilot's critical safety banner offers them ("Get help"): the server
 * picks the lines for the person's destination (`crisisLinesFor`) and sends
 * them with the banner, so every client shows the same help and none decides
 * it. Data, not code: a client names a line by its `id` (an i18n key per id)
 * and dials `dial`. Its shape (`CrisisLine`) is in `@tonnie/types`, which the
 * banner on the wire carries.
 *
 * `CRISIS_LINES` is a `Record<OpenDestination, …>`: opening a destination
 * (`OPEN_DESTINATIONS`) without declaring its lines does not compile.
 *
 * Sources (checked 21 Oct 2026):
 *   • 112 — the European emergency number, free, every EU member state, any
 *     phone: https://www.112.pt and https://digital-strategy.ec.europa.eu/en/policies/112
 *   • SNS 24 — 808 24 24 24, Portugal's National Health Service line, 24 hours,
 *     which includes psychological counselling: https://www.sns24.gov.pt
 */

import type { CrisisLine } from '@tonnie/types';

import { openDestinationOf, type OpenDestination } from './destinations';

const EUROPEAN_EMERGENCY: CrisisLine = {
  id: 'emergency',
  dial: '112',
  display: '112',
  availability: 'always',
};

/** Every open destination's lines — the first is the one to call in danger. */
export const CRISIS_LINES: Readonly<Record<OpenDestination, readonly CrisisLine[]>> = {
  PT: [
    EUROPEAN_EMERGENCY,
    { id: 'sns24', dial: '808242424', display: '808 24 24 24', availability: 'always' },
  ],
};

/**
 * For a person whose country is unknown, or a destination with no entry: 112
 * answers in every EU member state.
 */
export const EU_CRISIS_LINES: readonly CrisisLine[] = [EUROPEAN_EMERGENCY];

/** The lines for a person's destination (any case), or the European ones. */
export function crisisLinesFor(destination: string | null | undefined): readonly CrisisLine[] {
  const open = openDestinationOf(destination);
  return open ? CRISIS_LINES[open] : EU_CRISIS_LINES;
}

const DECLARED_LINES: readonly CrisisLine[] = [
  ...Object.values(CRISIS_LINES).flat(),
  ...EU_CRISIS_LINES,
];

/**
 * The declared line a stored one names (same id and number), or null — how a
 * banner kept in a turn's trace is read back: only a line the platform still
 * declares is offered again, never a number that has since changed.
 */
export function declaredCrisisLine(raw: unknown): CrisisLine | null {
  if (!raw || typeof raw !== 'object') return null;
  const { id, dial } = raw as { id?: unknown; dial?: unknown };
  return DECLARED_LINES.find((line) => line.id === id && line.dial === dial) ?? null;
}
